import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useSettingsStore, formatCurrencyWithSettings } from '@/stores/settingsStore';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date): string {
  const settings = useSettingsStore.getState().settings.general;
  const d = new Date(date);
  
  // Map format string to Intl options
  const formatMap: Record<string, Intl.DateTimeFormatOptions> = {
    'DD/MM/YYYY': { day: '2-digit', month: '2-digit', year: 'numeric' },
    'MM/DD/YYYY': { month: '2-digit', day: '2-digit', year: 'numeric' },
    'YYYY-MM-DD': { year: 'numeric', month: '2-digit', day: '2-digit' },
  };
  
  const options = formatMap[settings.dateFormat] || formatMap['DD/MM/YYYY'];
  
  // For YYYY-MM-DD format, we need to construct it manually
  if (settings.dateFormat === 'YYYY-MM-DD') {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  // For DD/MM/YYYY and MM/DD/YYYY, use locale-based formatting
  const locale = settings.dateFormat === 'MM/DD/YYYY' ? 'en-US' : 'en-GB';
  return d.toLocaleDateString(locale, options);
}

export function formatDateTime(date: string | Date | number | null | undefined): string {
  if (!date) return '-';
  try {
    const settings = useSettingsStore.getState().settings.general;
    const d = typeof date === 'number' ? new Date(date) : new Date(date);
    if (isNaN(d.getTime())) return '-';
    
    const is24Hour = settings.timeFormat === '24h';
    
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: !is24Hour,
    });
  } catch {
    return '-';
  }
}

export function formatRelativeTime(date: string | Date | number | null | undefined): string {
  if (!date) return '-';
  try {
    const d = typeof date === 'number' ? new Date(date) : new Date(date);
    if (isNaN(d.getTime())) return '-';
    
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin} min${diffMin > 1 ? 's' : ''} ago`;
    if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
    if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
    
    return formatDate(d);
  } catch {
    return '-';
  }
}

export function formatCurrency(amount: number): string {
  return formatCurrencyWithSettings(amount);
}

export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return `${str.slice(0, length)}...`;
}

export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    // Asset statuses
    AVAILABLE: 'bg-green-100 text-green-800',
    ASSIGNED: 'bg-blue-100 text-blue-800',
    DEPLOYED: 'bg-blue-100 text-blue-800',
    IN_STOCK: 'bg-green-100 text-green-800',
    IN_MAINTENANCE: 'bg-yellow-100 text-yellow-800',
    UNDER_REPAIR: 'bg-orange-100 text-orange-800',
    RETIRED: 'bg-gray-100 text-gray-800',
    DISPOSED: 'bg-red-100 text-red-800',
    LOST: 'bg-red-100 text-red-800',
    RESERVED: 'bg-purple-100 text-purple-800',
    // Ticket statuses
    OPEN: 'bg-blue-100 text-blue-800',
    IN_PROGRESS: 'bg-yellow-100 text-yellow-800',
    PENDING: 'bg-orange-100 text-orange-800',
    ON_HOLD: 'bg-gray-100 text-gray-800',
    RESOLVED: 'bg-green-100 text-green-800',
    CLOSED: 'bg-gray-100 text-gray-800',
    CANCELLED: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

export function getPriorityColor(priority: string): string {
  const colors: Record<string, string> = {
    LOW: 'bg-gray-100 text-gray-800',
    MEDIUM: 'bg-blue-100 text-blue-800',
    HIGH: 'bg-orange-100 text-orange-800',
    URGENT: 'bg-red-100 text-red-800',
    CRITICAL: 'bg-red-200 text-red-900',
  };
  return colors[priority] || 'bg-gray-100 text-gray-800';
}
